import { KeyControl } from "@pathwright/utils/src/keyboard/types"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import { useFormikContext } from "formik"
import { useRef } from "react"
import { useIsSubmissionDisabled } from "../SubmitButton"

type HandledKeyControl = Extract<KeyControl, "Meta+Enter" | "Enter">

type KeyboardFormSubmitProps = {
  keyControls?: HandledKeyControl[]
}

function KeyboardFormSubmitInner({
  keyControls = ["Meta+Enter"]
}: KeyboardFormSubmitProps) {
  const ref = useRef<HTMLDivElement>(null)
  const context = useFormikContext()

  function handleEnterKey(e: KeyboardEvent) {
    // The "DOM anchor" for this instance of KeyboardFormSubmit must
    // be contained by the closest form in order to handle the event.
    // This handles the case where multiple forms exist in the DOM
    // but we only want to submit the one in which the user executed
    // the keyboard shortcut.
    const closestFormContainsTarget = !!ref.current
      ?.closest?.("form")
      ?.contains(e.target as Node)

    if (closestFormContainsTarget) {
      // `stopPropagation` is needed with capture: rich text editors (e.g. Quill) often call
      // `preventDefault` on Enter, so a bubble `window` listener would never see the event
      // (`!e.defaultPrevented`). Stopping propagation also keeps the key from reaching the
      // editor after our window capture handler runs.
      e.stopPropagation()
      try {
        context.submitForm?.()
      } catch (error) {
        console.warn(
          "Failed attempt to submit form via keyboard shortcut due to error:",
          error
        )
      }
      // Prevent other handlers from being called.
      return true
    }

    return false // Allow other handlers to be called.
  }

  useKeyControl(keyControls, handleEnterKey, { capture: true })

  // Render a dummy element which we can use as a DOM anchor
  // to calibrate our submit handler to.
  return <div className="keyboard-form-submit-anchor" ref={ref} />
}

// Submits formik form when Meta+Enter keyboard keys are pressed.
// NOTE: if Formik is not provided the onSubmit prop, using this
// component will result in an unhandled error when user uses
// keyboard shortcut.
function KeyboardFormSubmit({ keyControls }: KeyboardFormSubmitProps) {
  // Only enable handler when form can be submitted if form submission is enabled.
  return useIsSubmissionDisabled() ? null : (
    <KeyboardFormSubmitInner keyControls={keyControls} />
  )
}

export default KeyboardFormSubmit
